import { rankProducts } from './project-seo'
import type {
  CanonicalReportProduct,
  CanonicalResearchReportDocument,
} from './types'

/**
 * Pre-publish vetting for auto-published research reports.
 *
 * The readiness gates in status.ts decide whether a report *can* render; the
 * web re-checks them on every request, so nothing content-related belongs
 * there (a new rule would 404 live pages). This check runs once, before the
 * first automatic publish, and holds a report for human review when the
 * picks a reader sees are ones the research itself rejected, can't be bought,
 * or come from a replica seller.
 *
 * Calibrated 2026-10-02 against all 393 live reports plus the rejected
 * Hermès and Cartier rebuilds: both rebuilds are held, 32 live reports (8%)
 * would have been. Results: docs/features/active/research-content-factory/
 * publish-vetting.md.
 */

export const TOP_PICK_COUNT = 4
export const VISIBLE_PRODUCT_COUNT = 7

export type PublishVettingCode =
  | 'pick-rejected-by-research'
  | 'pick-has-no-buy-link'
  | 'pick-has-no-price'
  | 'blocked-seller'

export interface PublishVettingIssue {
  code: PublishVettingCode
  productSlug: string
  detail: string
}

export interface PublishVettingResult {
  ok: boolean
  issues: PublishVettingIssue[]
}

// Phrases the research writes when a pick breaks the brief. Stems like
// "disqualif" alone also match "passes all disqualifier checks", so each
// pattern is specific.
const REJECTION_PATTERNS: Array<{ label: string; pattern: RegExp }> = [
  {
    label: 'flagged as a disqualifier',
    pattern:
      /\b(hard|likely|critical|stated|clear|automatic|major)\s+(\S+\s+)?disqualifier|\bdisqualif(ied|ies|y)\b|\btrigger\w*\s[^.;]{0,30}disqualifier|\bhits?\s[^.;]{0,30}disqualifier|\bdisqualifier\s(flags?|for this)/i,
  },
  {
    label: 'may not exist',
    pattern:
      /\bdoes not (actually )?exist|\bnon-?existent\b|\bcould not be (confirmed|verified) as a (live|real)/i,
  },
  { label: 'grey-market listing', pattern: /\b(grey|gray)[- ]market\b/i },
  {
    label: 'unavailable',
    pattern:
      /\b(is|was|appears|currently|now) (officially )?(sold out|discontinued|out of stock|no longer (sold|available))\b|\bhas (been )?(officially )?discontinued\b/i,
  },
]

// A negation cancels a rejection phrase only when it is directly in front.
// "passes all brief disqualifiers" allows a word or two between; "no",
// "without" and "not" must be adjacent, so "No stock; hard disqualifier" and
// "no heel strap, making it a disqualified candidate" are still held.
const NEGATION_BEFORE =
  /(\b(pass(es|ing|ed)?|clears?|clearing|avoids?)\s+(all\s+|any\s+|the\s+)?(\S+\s+)?|\b(no|without|not|zero)\s+(any\s+|a\s+)?)$/i

// Replica marketplaces. Matched against buy-link domains and brand names
// (the Cartier rebuild listed "Replica4Less" as a brand with no link).
const BLOCKED_SELLER = /dhgate|replica/i

function pickSummary(
  document: CanonicalResearchReportDocument,
  product: CanonicalReportProduct
): string {
  // Same precedence as the public top-pick card (project-seo.ts).
  return (
    document.analysis?.productSummaries?.find(
      (entry) => entry.slug === product.slug
    )?.summary ??
    product.assessment?.summary ??
    product.ranking?.highlightText ??
    product.ranking?.rationale ??
    ''
  )
}

export function findResearchRejection(text: string): string | null {
  for (const { label, pattern } of REJECTION_PATTERNS) {
    const global = new RegExp(pattern.source, 'gi')
    for (const match of text.matchAll(global)) {
      const index = match.index ?? 0
      const before = text.slice(Math.max(0, index - 60), index)
      if (NEGATION_BEFORE.test(before)) continue
      const excerpt = text
        .slice(Math.max(0, index - 60), index + match[0].length + 30)
        .trim()
      return `${label}: "…${excerpt}…"`
    }
  }
  return null
}

/** Retailer domain of a buy link: the `shopKey` param of a /redirect URL, else its host. */
export function buyLinkDomain(url: string): string {
  try {
    const parsed = new URL(url, 'https://dupe.com')
    const shopKey = parsed.searchParams.get('shopKey')
    const host = shopKey ? new URL(shopKey).hostname : parsed.hostname
    return host.replace(/^www\./, '').toLowerCase()
  } catch {
    return ''
  }
}

function hasPrice(product: CanonicalReportProduct): boolean {
  const { msrp, observedLow, observedHigh } = product.pricing ?? {}
  if (msrp || observedLow || observedHigh) return true
  return (product.retailerLinks?.buyLinks ?? []).some((link) => link.price)
}

export function vetResearchReportForPublish(
  document: CanonicalResearchReportDocument
): PublishVettingResult {
  const issues: PublishVettingIssue[] = []
  const ranked = rankProducts(document.products ?? [])

  for (const product of ranked.slice(0, TOP_PICK_COUNT)) {
    const rejection = findResearchRejection(pickSummary(document, product))
    if (rejection) {
      issues.push({
        code: 'pick-rejected-by-research',
        productSlug: product.slug,
        detail: rejection,
      })
    }
    const buyLinks = product.retailerLinks?.buyLinks ?? []
    if (buyLinks.length === 0) {
      issues.push({
        code: 'pick-has-no-buy-link',
        productSlug: product.slug,
        detail: 'top pick has no buy link',
      })
    } else if (!hasPrice(product)) {
      issues.push({
        code: 'pick-has-no-price',
        productSlug: product.slug,
        detail: 'top pick has no price',
      })
    }
  }

  for (const product of ranked.slice(0, VISIBLE_PRODUCT_COUNT)) {
    const blockedDomain = (product.retailerLinks?.buyLinks ?? [])
      .map((link) => buyLinkDomain(link.url))
      .find((domain) => BLOCKED_SELLER.test(domain))
    const blocked =
      blockedDomain ??
      (BLOCKED_SELLER.test(product.brand ?? '') ? product.brand : null)
    if (blocked) {
      issues.push({
        code: 'blocked-seller',
        productSlug: product.slug,
        detail: `replica seller: ${blocked}`,
      })
    }
  }

  return { ok: issues.length === 0, issues }
}

export function summarizePublishVetting(result: PublishVettingResult): string {
  const lines = result.issues.map(
    (issue) => `${issue.productSlug} — ${issue.detail}`
  )
  return `Held for review before publish: ${lines.join('; ')}`
}
